import { useEffect, useRef, useState } from "react";

/**
 * SettingsSelect — the app's established custom single-select dropdown
 * (the `cdd` pattern used across the Scheduler), scoped to the Settings
 * redesign. We never use native <select> in this UI.
 *
 * Props:
 *   value       current selected value
 *   onChange    (value) => void
 *   options     [{ value, label }]
 *   placeholder shown when nothing is selected
 *   disabled    renders a non-interactive, muted control (e.g. "Coming soon")
 */
const Chevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default function SettingsSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [open]);

  // Reset the search query whenever the dropdown closes.
  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const sel = options.find((o) => o.value === value);
  const searchable = options.length > 6;
  const query = q.trim().toLowerCase();
  const filtered = query
    ? options.filter((o) => String(o.label).toLowerCase().includes(query))
    : options;

  return (
    <div className={`cdd${open ? " open" : ""}${disabled ? " disabled" : ""}`} ref={ref}>
      <button
        type="button"
        className="cdd-btn"
        disabled={disabled}
        onClick={(e) => {
          if (disabled) return;
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {sel ? <span>{sel.label}</span> : <span className="cdd-ph">{placeholder}</span>}
        <Chevron />
      </button>
      <div className="cdd-menu" role="listbox">
        {searchable ? (
          <div className="cdd-search" onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              value={q}
              autoFocus
              placeholder="Search…"
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        ) : null}
        {filtered.length === 0 ? (
          <div className="cdd-empty">No matches</div>
        ) : (
          filtered.map((o) => (
            <div
              key={o.value || "__empty"}
              role="option"
              aria-selected={o.value === value}
              className={`cdd-opt${o.value === value ? " sel" : ""}`}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              <div className="co-name">{o.label}</div>
              <span className="co-chk"><Check /></span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
